import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  fetchRestaurantGlobalFees,
} from "../../../../redux/thunk";
import { VendorSpecificFee, RestaurantOverride } from "../types";
import { formatCurrency, formatPercentage, getDeliveryModelBadgeColor } from "../mockData";
import LoadingSpinner from "../../../../components/elements/LoadingSpinner";
import { HiPencil } from "react-icons/hi";
import toast from "react-hot-toast";

const VendorSpecificFees: React.FC = () => {
  const dispatch = useAppDispatch();
  const { restaurantGlobalFees, loading, error } = useAppSelector((state) => state.fees);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredVendors, setFilteredVendors] = useState<RestaurantOverride[]>([]);

  useEffect(() => {
    dispatch(fetchRestaurantGlobalFees());
  }, [dispatch]);

  useEffect(() => {
    if (restaurantGlobalFees?.restaurantOverrides) {
      const filtered = restaurantGlobalFees.restaurantOverrides.filter((vendor) =>
        vendor.restaurantId.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredVendors(filtered);
    } else {
      setFilteredVendors([]);
    }
  }, [restaurantGlobalFees, searchTerm]);

  const handleEditFee = (vendorId: string) => {
    // This would typically open a modal or navigate to an edit page
    console.log(`Edit fee for vendor: ${vendorId}`);
    // For now, we'll just log it - this would be implemented with a modal
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Search and Bulk Update */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold">Restaurant-Specific Fee Configuration</h3>
          <p className="text-sm text-gray-500">
            Manage commission rates, delivery fees, and service charges for individual restaurants
          </p>
        </div>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search restaurants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border rounded-md w-64"
          />
          <button className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-dark transition-colors">
            Bulk Update
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Restaurant
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commission Rate
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivery Model
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service Charge
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVendors.map((vendor) => (
                <tr key={vendor._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Restaurant
                      </div>
                      <div className="text-sm text-gray-500">{vendor.restaurantId}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {vendor.commissionRate}%
                      </span>
                      <span className="ml-2 text-xs text-gray-500">Custom Rate</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Custom
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Custom
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">
                        Delivery: {formatCurrency(vendor.deliveryRate)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Service: {vendor.serviceCharge}%
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEditFee(vendor.restaurantId)}
                      className="flex items-center gap-2 text-secondary hover:text-secondary-dark"
                    >
                      <HiPencil className="w-4 h-4" />
                      Edit Fee
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredVendors.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchTerm ? 'No restaurants found matching your search.' : 'No vendor-specific fees configured yet.'}
            </p>
            <button className="mt-4 px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-dark transition-colors">
              Add Restaurant Fee
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-500">Total Restaurants</h4>
          <p className="text-2xl font-bold text-gray-900">{filteredVendors.length}</p>
        </div>
        <div className="bg-white p-6 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-500">Custom Rates</h4>
          <p className="text-2xl font-bold text-gray-900">
            {filteredVendors.length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-500">Avg Commission</h4>
          <p className="text-2xl font-bold text-gray-900">
            {filteredVendors.length > 0 
              ? `${(filteredVendors.reduce((acc, v) => acc + v.commissionRate, 0) / filteredVendors.length).toFixed(1)}%`
              : '0%'
            }
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-500">Global Commission</h4>
          <p className="text-2xl font-bold text-gray-900">
            {restaurantGlobalFees ? `${restaurantGlobalFees.commissionRate}%` : '0%'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VendorSpecificFees;